import { DateFormat, formatDate } from '@leland-dev/leland-ui-library';
import groq from 'groq';
import Link from 'next/link';
import type { FC } from 'react';

import type { Category } from '../../types/cmsTypes';
import { formatName } from '../../utils/formatters';
import { getCoachProfileUrl, getLibraryPageUrl } from '../../utils/routing';
import { getUrlString } from '../../utils/url';
import ImageWithBlur from '../image/ImageWithBlur';

import { type ArticleHeroCoachFragment } from './__generated-gql-types__/ArticleHero.generated';

export const ARTICLE_HERO_CATEGORY_QUERY_SELECTION = groq`
  _id,
  slug,
  title,
`;

interface Props {
  className?: string;
  title: string;
  subtitle: string;
  categories?: Array<Pick<Category, '_id' | 'slug' | 'title'>>;
  coach?: ArticleHeroCoachFragment;
  writtenAt: string;
}

const ArticleHero: FC<Props> = ({
  className,
  title,
  subtitle,
  categories,
  coach,
  writtenAt,
}) => {
  const categoryToShow = categories?.length === 1 ? categories[0] : null;

  const headlineFromCategory = coach?.categoryInformationList?.find(
    (catInfo) => catInfo.category.slug === categoryToShow?.slug?.current,
  )?.headline;

  return (
    <div className={`${className} px-4 lg:w-50%`}>
      <div className="mx-auto flex max-w-xl flex-col items-center lg:p-20">
        <div className="mb-4 flex flex-wrap items-center font-medium uppercase text-leland-primary">
          <Link
            href={getLibraryPageUrl({})}
            className="link tracking-wider"
            id="article_hero--view_library"
          >
            Library
          </Link>
          {categoryToShow?.slug && categoryToShow.title ? (
            <>
              <span className="mx-2">/</span>
              <Link
                href={getLibraryPageUrl({ slug: categoryToShow.slug.current })}
                className="link tracking-wider"
                id="article_hero--view_category_library"
              >
                {categoryToShow.title}
              </Link>
            </>
          ) : null}
        </div>

        <h1 className="mb-4 w-full text-center text-4xl font-medium text-leland-gray-dark">
          {title}
        </h1>

        <p className="mb-6 text-center text-xl text-leland-gray-light">
          {subtitle}
        </p>

        {coach ? (
          <a
            className="mb-1 flex cursor-pointer items-center justify-center"
            href={getUrlString(
              getCoachProfileUrl(coach.slug),
              categories?.length === 1
                ? { category: categories[0].slug?.current }
                : undefined,
            )}
            target="_blank"
            rel="noreferrer"
            id="article_hero--view_coach_profile"
          >
            {coach.user.pictureLink ? (
              <div className="relative mr-2 size-5">
                <ImageWithBlur
                  className="rounded-full object-cover"
                  src={coach.user.pictureLink}
                  // w/h-14 corresponds to 3.5rem
                  sizes={{ dflt: '3.5rem' }}
                  alt={formatName(coach.user)}
                  fill
                />
              </div>
            ) : null}
            <p className="mb-1 text-lg font-medium text-leland-gray-dark">
              By&nbsp;
              <span>{formatName(coach.user)}</span>
            </p>
          </a>
        ) : null}
        {coach && categoryToShow?.slug?.current ? (
          <p className="mb-1 text-center text-leland-gray-light">
            {headlineFromCategory}
          </p>
        ) : null}

        <p className="text-leland-gray-light">
          Posted {formatDate(new Date(writtenAt), DateFormat.LONG_STANDARD)}
        </p>
      </div>
    </div>
  );
};

export default ArticleHero;
